import { Layout, Menu, Spin, Badge, message, Tooltip, Input, Drawer } from 'antd';
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { makeRequestStateless } from '@utils/helpers';
import { getNavigationReq } from '@mods/common/requests';
import { encryptLocalData, decryptLocalData, checkPermissions } from '@comps/commonFunctions';
import './navigation.css';
import { countNavNotificationsReq, getRolePermissionsReq } from './requests';
import { pusherInitializer } from '@comps/commonFunctions';
import { PlusOutlined, MenuFoldOutlined, MenuUnfoldOutlined, RightOutlined } from '@ant-design/icons';
import { MinimizeOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { clearFilterOptions } from '../../../redux/filterOptionsSlice';
import {
  buildNavigationTree,
  buildQuickMenuGroups,
  filterNavigationTree,
  findActiveLineage,
  navigationItemLabel,
  navItemPath,
  normalizeNavKey,
} from '../navigation/navigationTree';

const langKey = 'navigation';
const { Sider } = Layout;
const DEFAULT_NAV_ICON = '/sidebaricon/List.svg';
// Nothing is pinned to the bottom of the sidebar any more: Admin & Settings
// sits inline with the other menu items, and What's New moved to the top header.
const BOTTOM_NAV_KEYS = [];
const HEADER_NAV_KEYS = ['whatsnew'];

const NavSide = (props) => {
  const hasReportedReadyRef = useRef(false);
  const searchModuleRef = useRef(null);
  const quickMenuOpenTimerRef = useRef(null);
  const quickMenuCloseTimerRef = useRef(null);
  const intl = useIntl();
  const {
    isMobileViewport = false,
    mobileNavOpen = false,
    setMobileNavOpen,
    onNavigationChange,
  } = props;

  const [menuFilter, setMenuFilter] = useState('');
  const authUser = useSelector((state) => state.auth.authUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  const [navigation, setNavigation] = useState({});
  const theme = 'light';
  const [current, setCurrent] = useState(localStorage.getItem('selectedMenuItem') || '');
  const [currentModuleKey, setModuleKey] = useState(localStorage.getItem('modulekey') || getLastSubstring(location.pathname));
  const [notificationModules, setNotificationsModules] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [quickMenu, setQuickMenu] = useState(null);

  const dispatch = useDispatch();

  const canViewReports = checkPermissions('reportsAll');

  const navigationTree = useMemo(
    () => buildNavigationTree(
      navigation.permissions,
      navigation.custom_pay_items,
      { includeScopedReports: !!canViewReports }
    ),
    [navigation.permissions, navigation.custom_pay_items, canViewReports]
  );
  const activeLineage = useMemo(
    () => findActiveLineage(navigationTree, location.pathname, currentModuleKey),
    [navigationTree, location.pathname, currentModuleKey]
  );
  const quickMenuGroups = useMemo(
    () => buildQuickMenuGroups(quickMenu?.item),
    [quickMenu]
  );
  const selectedMainMenuKeys = useMemo(() => {
    const root = activeLineage?.[0];
    if (!root) return [];

    const path = navItemPath(root);
    const url = path === '/' ? '' : String(path || '').replace(/^\//, '');
    const moduleKey = root.module_key === 'dashboard' ? '' : root.module_key;
    return [`${url}|${moduleKey}`];
  }, [activeLineage]);

  useEffect(() => {
    onNavigationChange?.({
      navigation,
      notificationModules,
      loading,
    });
  }, [loading, navigation, notificationModules, onNavigationChange]);

  const normalizeNotificationKey = useCallback((value) => {
    if (value === null || value === undefined) return "";

    return String(value)
      .replace(/^app\./, "")
      .replace(/[_\s./|-]/g, "")
      .toLowerCase();
  }, []);

  useEffect(() => () => {
    window.clearTimeout(quickMenuOpenTimerRef.current);
    window.clearTimeout(quickMenuCloseTimerRef.current);
  }, []);

  useEffect(() => {
    setQuickMenu(null);
  }, [location.pathname]);

  const getNotificationValue = (module) => {
    const count = module?.value ?? module?.count ?? module?.total ?? module?.notification_count ?? 0;
    const parsedCount = Number(count);

    return Number.isNaN(parsedCount) ? 0 : parsedCount;
  };

  const getNotificationLookupKeys = useCallback(
    (item) =>
      [
        item?.module_key,
        item?.moduleKey,
        item?.nav_key,
        item?.key,
        item?.name,
        item?.code,
        item?.sref,
        item?.identifier,
        item?.tag,
        item?.tags,
      ]
        .map(normalizeNotificationKey)
        .filter(Boolean),
    [normalizeNotificationKey]
  );

  const getNavItemLookupKeys = useCallback(
    (item) =>
      [
        getModuleKey(item),
        item?.module_key,
        item?.name,
        item?.code,
        item?.sref,
        item?.identifier,
        item?.tag,
        item?.tags,
      ]
        .map(normalizeNotificationKey)
        .filter(Boolean),
    [normalizeNotificationKey]
  );

  // ====================== OPTIMIZED NOTIFICATION COUNT (pure functions - no global mutable var) ======================
  const getNotificationCount = useCallback((modules, targetKeys) => {
    if (!modules || modules.length === 0) return 0;

    const lookupKeys = Array.isArray(targetKeys) ? targetKeys : [targetKeys].filter(Boolean);
    let total = 0;

    for (const module of modules) {
      const moduleKeys = getNotificationLookupKeys(module);
      const directMatch = moduleKeys.some((key) => lookupKeys.includes(key));

      if (directMatch) {
        total += getNotificationValue(module);
      }

      if (module.sub_modules?.length > 0) {
        total += getNotificationCount(module.sub_modules, lookupKeys);
      }
    }

    return total;
  }, [getNotificationLookupKeys]);

  const getNavNotificationCount = useCallback(
    (item) => {
      return getNotificationCount(notificationModules, getNavItemLookupKeys(item));
    },
    [notificationModules, getNotificationCount, getNavItemLookupKeys]
  );

  const countNotify = useCallback((item) => {
    const count = getNavNotificationCount(item);
    if (!count) return null;

    return (
      <Badge
        className="nav-notification-badge"
        size="small"
        count={ count }
        overflowCount={ 99 }
        color="var(--red)"
      />
    );
  }, [getNavNotificationCount]);

  // ====================== KEYDOWN & RESIZE (fixed - was broken in original) ======================
  useEffect(() => {
    // Ctrl + M to focus quick search
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'm') {
        event.preventDefault();
        searchModuleRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Resize handler (now properly inside effect)
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Initial resize
    handleResize();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Keep desktop aligned with the V10 rail; mobile drawer still renders expanded labels.
  useEffect(() => {
    setCollapsed(true);
  }, [windowWidth]);

  // ====================== LOAD NAVIGATION (only on mount + pusher events) ======================
  const getNavWithPermissions = useCallback(() => {
    const nav = decryptLocalData('navigation');
    if (nav) {
      if (authUser?.super_admin) {
        nav.permissions.push(getReportsPermissions());
      }

      setNavigation(nav);
      setLoading(false);
    } else {
      setLoading(true);
      makeRequestStateless(getNavigationReq, null, onGetNavigation, null);
    }
  }, [authUser]);

  // Initial load only; workspace navigation reacts to the shared navigation state.
  useEffect(() => {
    getNavWithPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && !hasReportedReadyRef.current) {
      hasReportedReadyRef.current = true;
      props.onReady?.();
    }
  }, [loading, props]);

  // ====================== NOTIFICATIONS ======================
  useEffect(() => {
    getNavNotificationsCount('notifications');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pusher for notifications
  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user?.tenant) return;

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    channel.bind('refresh-navigation-event', () => {
      getNavNotificationsCount('notifications');
    });

    return () => {
      channel.unbind('refresh-navigation-event');
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [authUser]);

  // Pusher for role permissions update
  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user?.tenant) return;

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    channel.bind('RolePermissionsUpdatedEvent', (data) => {
      if (data && Number(data.role_id) === authUser?.role_id) {
        localStorage.setItem('navigation', '');
        message.info('Role Permissions Updated!');
        getRolePermissions(authUser.role_id);
        getNavWithPermissions();
      }
    });

    return () => {
      channel.unbind('RolePermissionsUpdatedEvent');
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [authUser]);

  const getRolePermissions = (roleId) => {
    setLoading(true);
    makeRequestStateless(getRolePermissionsReq, roleId, onGetPermissions, null);
  };

  const onGetPermissions = (data) => {
    encryptLocalData('permissions', data);
    setLoading(false);
  };

  const getNavNotificationsCount = (navKey) => {
    makeRequestStateless(countNavNotificationsReq, navKey, onSuccessNavNotifications, null);
  };

  const onSuccessNavNotifications = (data, res) => {
    const modules = Array.isArray(data)
      ? data
      : data?.sub_modules || data?.data?.sub_modules || res?.data?.sub_modules || [];
    setNotificationsModules(modules);
  };

  // ====================== REPORTS FOR SUPER ADMIN ======================
  const getReportsPermissions = () => ({
    sref: "app.reports.all_reports",
    module_key: "reports",
    name: "REPORTS",
    icon: "fa fa-bar-chart",
    tag: null,
    id: 169,
    hasPermisson: 1,
    parentId: null,
    sort_order: 233,
    super_admin: 0,
    hide_in_nav: 0,
    is_mobile: 0,
    role_id: 4,
    identifier: "_"
  });

  const onGetNavigation = (data, res) => {
    setLoading(false);
    encryptLocalData('navigation', res.data);
    if (authUser?.super_admin) {
      res.data.permissions.push(getReportsPermissions());
    }
    setNavigation(res.data);
  };

  // ====================== TRANSLATE & ICON ======================
  const formatNavLabel = useCallback((value) => {
    const text = String(value || '').replace(/_/g, ' ').replace(/-/g, ' ').trim();
    if (!text) return '';

    const normalized = normalizeNavKey(text);
    if (normalized === 'myspace') return 'My Space';
    if (normalized === 'whatsnew') return "What's New";

    return text
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
      .replace(/\bHr\b/g, 'HR')
      .replace(/\bId\b/g, 'ID');
  }, []);

  const translateNav = useCallback((item) => {
    if (item?.code !== undefined) return formatNavLabel(item?.name || '');

    const translationKey = `${langKey}.${String(item?.module_key || '').replace(/-/g, "")}`;
    const translatedText = intl.messages?.[translationKey]
      ? intl.formatMessage({ id: translationKey })
      : '';

    return formatNavLabel(translatedText || item?.name || item?.module_key || '');
  }, [formatNavLabel, intl]);

  const renderSvgNavIcon = (key) => (
    <img
      src={ `/sidebaricon/${key}.svg` }
      alt="icon"
      className="nav-icon"
      onError={ ({ currentTarget }) => {
        currentTarget.onerror = null;
        currentTarget.src = DEFAULT_NAV_ICON;
      } }
    />
  );

  const isCollapsed = true;

  const getNavIcon = (item, type) => {
    const key = (item.navIconKey || item.module_key).replace(/-/g, "");
    let icon = null;

    if (type === "subMenu") {
      icon = renderSvgNavIcon(key);
    }
    if (type === "menu") {
      if (item.code !== undefined) {
        icon = item.icon === "+" ? <PlusOutlined className="nav-icon" /> : item.icon === '-' ? <MinimizeOutlined className="nav-icon" /> : item.icon;
      } else {
        icon = renderSvgNavIcon(key);
      }
    }

    if (!icon) return null;

    const count = getNavNotificationCount(item);
    if (!isCollapsed || !count) return icon;

    return (
      <Badge
        className="nav-icon-notification-badge"
        size="small"
        count={ count }
        overflowCount={ 99 }
        color="var(--red)"
      >
        <span className="nav-icon-badge-anchor">{ icon }</span>
      </Badge>
    );
  };

  const getModuleKey = (item) => (item.module_key === 'dashboard' ? '' : item.module_key);

  const cancelQuickMenuClose = () => {
    window.clearTimeout(quickMenuCloseTimerRef.current);
  };

  const scheduleQuickMenuOpen = (item, target, immediate = false) => {
    if (isMobileViewport || !item?.children?.length) return;

    window.clearTimeout(quickMenuCloseTimerRef.current);
    window.clearTimeout(quickMenuOpenTimerRef.current);

    const open = () => {
      const bounds = target?.getBoundingClientRect();
      setQuickMenu({
        item,
        top: Math.max(12, bounds?.top ?? 12),
      });
    };

    if (immediate) open();
    else quickMenuOpenTimerRef.current = window.setTimeout(open, 240);
  };

  const scheduleQuickMenuClose = () => {
    window.clearTimeout(quickMenuOpenTimerRef.current);
    window.clearTimeout(quickMenuCloseTimerRef.current);
    quickMenuCloseTimerRef.current = window.setTimeout(() => setQuickMenu(null), 180);
  };

  const openQuickMenuItem = (item) => {
    const path = navItemPath(item);
    if (!path) return;

    setCurrentModuleKey(getModuleKey(item));
    setCurrent(path.replace(/^\//, ''));
    setQuickMenu(null);
    navigate(path);
  };

  // ====================== FORMAT PERMISSIONS (pure - no side effects, no mutation of source data) ======================
  const formatPermissions = useCallback((data, placement = 'primary') => {
    if (!data) return [];

    const result = buildNavigationTree(
      data,
      navigation.custom_pay_items,
      { includeScopedReports: !!canViewReports }
    );
    const filteredResult = filterNavigationTree(result, menuFilter, translateNav);
    const placementItems = filteredResult.filter((item) => {
      const navKey = normalizeNavKey(item.module_key || item.name);
      if (HEADER_NAV_KEYS.includes(navKey)) return false;

      const isBottomItem = BOTTOM_NAV_KEYS.includes(navKey);
      return placement === 'bottom' ? isBottomItem : !isBottomItem;
    });

    return renderMenuItems(placementItems);
  }, [
    navigation.custom_pay_items,
    menuFilter,
    notificationModules,
    collapsed,
    isMobileViewport,
    currentModuleKey,
    countNotify,
    getNavNotificationCount,
    translateNav,
    canViewReports,
  ]);

  function renderMenuItems(data) {
    return data.map((item) => {
      const url = String(navItemPath(item) || '').replace(/^\//, '');
      const itemKey = `${url}|${getModuleKey(item)}`;
      const menuKey = item.navMenuKey || itemKey;

      let cleanKey = itemKey.replace("//", "");
      if (cleanKey === 'dashboard|') cleanKey = '';
      const finalPath = `/${cleanKey.replace(/\|(.*)/, "")}`;

      return (
        <Menu.Item
          key={ menuKey }
          title={ false }
          modulekey={ getModuleKey(item) }
          navpath={ finalPath }
          originitemkey={ itemKey }
          icon={ getNavIcon(item, "menu") }
          aria-haspopup={ item.children?.length ? 'menu' : undefined }
          aria-expanded={ quickMenu?.item === item }
          onMouseEnter={ (info) => scheduleQuickMenuOpen(item, info?.domEvent?.currentTarget ?? info?.currentTarget) }
          onMouseLeave={ scheduleQuickMenuClose }
          onFocus={ (info) => scheduleQuickMenuOpen(item, info?.domEvent?.currentTarget ?? info?.currentTarget, true) }
        >
          <a
            href={ finalPath }
            style={ { color: 'inherit', display: 'block', width: '100%', textDecoration: 'none' } }
            onClick={ (e) => {
              if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
              }
            } }
          >
            <div className="nav-item-content">
              <span className="nav-item-text">{ translateNav(item) }</span>
              <span className="nav-item-count">{ countNotify(item) }</span>
            </div>
          </a>
        </Menu.Item>
      );
    });
  }

  // ====================== MEMOIZED MENU (biggest performance win) ======================
  const renderedPrimaryMenuItems = useMemo(() => {
    return formatPermissions(navigation.permissions, 'primary');
  }, [formatPermissions, navigation.permissions, notificationModules, collapsed, currentModuleKey]);

  const renderedBottomMenuItems = useMemo(() => {
    return formatPermissions(navigation.permissions, 'bottom');
  }, [formatPermissions, navigation.permissions, notificationModules, collapsed, currentModuleKey]);

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleLogoClick = () => {
    setCurrentModuleKey('');
    setCurrent('');
    setQuickMenu(null);
    navigate('/');

    if (isMobileViewport) {
      setMobileNavOpen?.(false);
    }
  };

  const setCurrentModuleKey = (modulekey) => {
    setModuleKey(modulekey);
    localStorage.setItem('modulekey', modulekey);
    dispatch(clearFilterOptions());
  };

  function getLastSubstring(path) {
    const parts = path.split('/');
    return parts.pop();
  }

  const onClickMenu = (key, itemProps) => {
    let cleanKey = (itemProps?.originitemkey || key).replace("//", "");
    if (cleanKey === 'help_center') {
      window.open('http://help.klokhr.com', '_blank');
      return;
    }
    if (cleanKey === 'dashboard|') cleanKey = '';

    const modulekey = itemProps?.modulekey ?? getModuleKeyFromMenuKey(cleanKey);
    const navPath = itemProps?.navpath || `/${cleanKey.replace(/\|(.*)/, "")}`;

    setCurrentModuleKey(modulekey);
    navigate(navPath);
    setCurrent(cleanKey.replace(/\|(.*)/, ""));

    if (isMobileViewport) {
      setMobileNavOpen?.(false);
    }
  };

  // ====================== SAVE MENU STATE TO LOCALSTORAGE (separate effects - no longer inside getNavWithPermissions) ======================
  useEffect(() => {
    localStorage.setItem('selectedMenuItem', current);
  }, [current]);

  const sidebarContent = (
    <>
      <div className="sidebar-brand">
        <button type="button" className="sidebar-brand__identity" onClick={ handleLogoClick }>
          { isCollapsed ? (
            <div className="sidebar-brand__compact-logo">
              <img src={ require('@assets/images/image170.png') } alt="KLOK" className="sidebar-brand__logo-mark" />
            </div>
          ) : (
            <div className="sidebar-brand__content">
              <div className="sidebar-brand__logo-wrap">
                <img src={ require('@assets/images/image170.png') } alt="logo" className="sidebar-brand__logo-mark" />
              </div>
              <div className="sidebar-brand__copy">
                <img src={ require('@assets/images/KLOK.png') } alt="logo" className="sidebar-brand__logo-text" />
                <span className="sidebar-brand__subtitle">Smart HR Operations</span>
              </div>
            </div>
          ) }
        </button>

        <div className="sidebar-brand__actions">
          { !isMobileViewport && !isCollapsed ? (
            <div
              onClick={ handleCollapse }
              className="sidebar-brand__toggle"
            >
              <Tooltip title={ isCollapsed ? 'Maximize Menu' : "Minimize Menu" } placement="right">
                { isCollapsed ? <MenuUnfoldOutlined style={ { color: 'currentColor', fontSize: 16 } } /> : <MenuFoldOutlined style={ { color: 'currentColor', fontSize: 16 } } /> }
              </Tooltip>
            </div>
          ) : null }
        </div>
      </div>

      <Spin spinning={ loading }>
        { !isCollapsed && (
          <div className="sidebar-tools__panel">
            <Input
              ref={ searchModuleRef }
              allowClear
              value={ menuFilter }
              onChange={ (event) => setMenuFilter(event.target.value) }
              placeholder="Search Menu... Ctrl + M"
              className="sidebar-tools__filter"
            />
            <div className="sidebar-tools__meta">
              { menuFilter ? <span>Filtering active</span> : '' }
            </div>
          </div>
        ) }

        <div className={ `menucontainer${isCollapsed ? ' menucontainer--collapsed' : ''}` }>
          <div className="menucontainer__primary">
            <Menu
              mode="inline"
              theme={ theme }
              selectedKeys={ selectedMainMenuKeys }
              inlineCollapsed={ isCollapsed }
              onClick={ ({ key, item }) => onClickMenu(key, item?.props) }
            >
              { renderedPrimaryMenuItems }
            </Menu>
          </div>

          { renderedBottomMenuItems.length > 0 ? (
            <div className="menucontainer__bottom">
              <Menu
                mode="inline"
                theme={ theme }
                selectedKeys={ selectedMainMenuKeys }
                inlineCollapsed={ isCollapsed }
                onClick={ ({ key, item }) => onClickMenu(key, item?.props) }
              >
                { renderedBottomMenuItems }
              </Menu>
            </div>
          ) : null }
        </div>
      </Spin>
    </>
  );

  return (
    <>
      { isMobileViewport ? (
        <Drawer
          placement="left"
          open={ mobileNavOpen }
          onClose={ () => setMobileNavOpen?.(false) }
          closable={ false }
          width={ 84 }
          className="app-navigation-drawer"
          rootClassName="app-navigation-drawer-root"
          bodyStyle={ { padding: 0, background: 'transparent' } }
        >
          <div className="app-navigation-drawer__inner app-navigation-drawer__inner--rail">
            { sidebarContent }
          </div>
        </Drawer>
      ) : (
        <Sider width={ 248 } collapsedWidth={ 84 } trigger={ null } collapsible collapsed={ collapsed } theme="light" className="app-navigation-sider">
          { sidebarContent }
        </Sider>
      ) }

      { !isMobileViewport && quickMenu && quickMenuGroups.length > 0 ? (
        <nav
          className={ `nav-quick-menu${quickMenuGroups.length === 1 ? ' nav-quick-menu--single-column' : ''}` }
          style={ {
            top: quickMenu.top,
            '--quick-menu-top': `${quickMenu.top}px`,
          } }
          aria-label={ `${navigationItemLabel(quickMenu.item)} quick navigation` }
          onMouseEnter={ cancelQuickMenuClose }
          onMouseLeave={ scheduleQuickMenuClose }
          onFocus={ cancelQuickMenuClose }
          onBlur={ (event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) scheduleQuickMenuClose();
          } }
          onKeyDown={ (event) => {
            if (event.key === 'Escape') {
              setQuickMenu(null);
              event.preventDefault();
            }
          } }
        >
          <div className="nav-quick-menu__header">
            <div>
              <span className="nav-quick-menu__eyebrow">Quick navigation</span>
              <h2>{ navigationItemLabel(quickMenu.item) }</h2>
            </div>
            <button
              type="button"
              className="nav-quick-menu__workspace-link"
              onClick={ () => openQuickMenuItem(quickMenu.item) }
            >
              Open workspace
              <RightOutlined aria-hidden="true" />
            </button>
          </div>

          <div className="nav-quick-menu__groups">
            { quickMenuGroups.map((group) => (
              <section className="nav-quick-menu__group" key={ group.key }>
                <h3>{ group.label }</h3>
                <div className="nav-quick-menu__links">
                  { group.items.map((item) => (
                    <button
                      type="button"
                      className="nav-quick-menu__link"
                      key={ item.id || item.module_key }
                      onClick={ () => openQuickMenuItem(item) }
                    >
                      <span>{ navigationItemLabel(item) }</span>
                      { getNavNotificationCount(item) > 0 ? (
                        <Badge
                          count={ getNavNotificationCount(item) }
                          overflowCount={ 99 }
                          size="small"
                        />
                      ) : null }
                    </button>
                  )) }
                </div>
              </section>
            )) }
          </div>
        </nav>
      ) : null }
    </>
  );
};

export default NavSide;

function getModuleKeyFromMenuKey(key = '') {
  const [, modulekey = ''] = String(key).split('|');
  return modulekey;
}
